import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
    chats: [],
    messages: [],
    activeChat: null,
    status: 'idle',
    error: null,
};

// Fetch all chats for the user
export const fetchChats = createAsyncThunk('chat/fetchChats', async (_, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.get('/api/chat', config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Initiate or access a chat
export const accessChat = createAsyncThunk('chat/accessChat', async ({ userId, productId }, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.post('/api/chat', { receiverId: userId, productId }, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Fetch messages for a specific chat
export const fetchMessages = createAsyncThunk('chat/fetchMessages', async (conversationId, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.get(`/api/message/${conversationId}`, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Send a message
export const sendMessage = createAsyncThunk('chat/sendMessage', async (messageData, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
        // Clean data for API
        const { tempId, sender, ...apiData } = messageData;
        const { data } = await axios.post('/api/message', apiData, config);
        // Return combined data to help reducer map tempId to real Id
        return { ...data, tempId };
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Mark chat as read
export const markChatRead = createAsyncThunk('chat/markChatRead', async (conversationId, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
        await axios.put('/api/chat/read', { conversationId }, config);
        return conversationId;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Respond to an offer
export const respondToOffer = createAsyncThunk('chat/respondToOffer', async ({ messageId, status }, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.put('/api/message/offer/respond', { messageId, status }, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Share phone number
export const shareNumber = createAsyncThunk('chat/shareNumber', async (conversationId, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${userInfo.token}` } };
        const { data } = await axios.put('/api/chat/share-number', { conversationId }, config);
        return data;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Delete a chat
export const deleteChat = createAsyncThunk('chat/deleteChat', async (conversationId, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = { headers: { Authorization: `Bearer ${userInfo.token}` } };
        await axios.delete(`/api/chat/${conversationId}`, config);
        return conversationId;
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

// Delete a message
export const deleteMessage = createAsyncThunk('chat/deleteMessage', async ({ messageId, deleteType }, { getState, rejectWithValue }) => {
    try {
        const { user: { userInfo } } = getState();
        if (!userInfo || !userInfo.token) {
            return rejectWithValue('Not authorized, please login');
        }
        const config = {
            headers: { Authorization: `Bearer ${userInfo.token}` },
            params: { deleteType } // Send as query param
        };
        console.log(`Sending delete request for ${messageId} with type ${deleteType}`);
        const { data } = await axios.delete(`/api/message/${messageId}`, config);
        return { messageId, deleteType, ...data };
    } catch (error) {
        return rejectWithValue(error.response && error.response.data.message ? error.response.data.message : error.message);
    }
});

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        setActiveChat: (state, action) => {
            state.activeChat = action.payload;
        },
        addMessage: (state, action) => {
            const exists = state.messages.some(m => m._id === action.payload._id);
            if (!exists) {
                state.messages.push(action.payload);
            }
        },
        updateMessage: (state, action) => {
            const index = state.messages.findIndex(m => m._id === action.payload._id);
            if (index !== -1) {
                state.messages[index] = action.payload;
            }
        },
        updateUnreadCount: (state, action) => {
            const { conversationId } = action.payload;
            const chat = state.chats.find(c => c._id === conversationId);
            if (chat) {
                // Logic handled by fetchChats usually
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchChats.pending, (state) => { state.status = 'loading'; })
            .addCase(fetchChats.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.chats = action.payload;
            })
            .addCase(fetchChats.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(accessChat.fulfilled, (state, action) => {
                if (!action.payload) {
                    console.error('accessChat fulfilled but payload is null/undefined');
                    return;
                }
                if (!state.chats.find((c) => c._id === action.payload._id)) {
                    state.chats.unshift(action.payload);
                }
                state.activeChat = action.payload;
            })
            .addCase(fetchMessages.fulfilled, (state, action) => {
                state.messages = action.payload;
            })
            .addCase(sendMessage.pending, (state, action) => {
                const { tempId, text, sender, conversationId, type, offerAmount } = action.meta.arg;
                if (tempId) {
                    const optimisticMessage = {
                        _id: tempId,
                        conversationId,
                        sender: sender || { _id: 'me', name: 'Me' }, // Fallback if sender not passed
                        text,
                        type: type || 'text',
                        offerAmount,
                        createdAt: new Date().toISOString(),
                        isOptimistic: true
                    };
                    state.messages.push(optimisticMessage);
                }
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                // Find optimistic message and replace it
                const { tempId } = action.payload;
                if (tempId) {
                    const index = state.messages.findIndex(m => m._id === tempId);
                    if (index !== -1) {
                        state.messages[index] = action.payload; // Replace with real data
                    } else {
                        // Fallback in case of race condition or if it was cleared
                        state.messages.push(action.payload);
                    }
                } else {
                    state.messages.push(action.payload);
                }
            })
            .addCase(markChatRead.fulfilled, (state, action) => {
                // Optimistic update or re-fetch handled in component
            })
            .addCase(respondToOffer.pending, (state, action) => {
                const { messageId, status } = action.meta.arg;
                const index = state.messages.findIndex(m => m._id === messageId);
                if (index !== -1) {
                    // Save previous status for rollback if needed
                    state.messages[index]._previousStatus = state.messages[index].offerStatus;
                    state.messages[index].offerStatus = status;
                }
            })
            .addCase(respondToOffer.fulfilled, (state, action) => {
                const index = state.messages.findIndex(m => m._id === action.payload._id);
                if (index !== -1) {
                    state.messages[index] = action.payload;
                    // Clean up temp property
                    delete state.messages[index]._previousStatus;
                }
            })
            .addCase(respondToOffer.rejected, (state, action) => {
                const { messageId } = action.meta.arg;
                const index = state.messages.findIndex(m => m._id === messageId);
                if (index !== -1 && state.messages[index]._previousStatus) {
                    // Rollback
                    state.messages[index].offerStatus = state.messages[index]._previousStatus;
                    delete state.messages[index]._previousStatus;
                }
            })
            .addCase(shareNumber.fulfilled, (state, action) => {
                state.activeChat = action.payload;
            })
            .addCase(deleteChat.fulfilled, (state, action) => {
                state.chats = state.chats.filter(c => c._id !== action.payload);
                if (state.activeChat && state.activeChat._id === action.payload) {
                    state.activeChat = null;
                    state.messages = [];
                }
            })
            .addCase(deleteMessage.fulfilled, (state, action) => {
                const { messageId, deleteType, isDeleted } = action.payload;
                if (deleteType === 'me') {
                    state.messages = state.messages.filter(m => m._id !== messageId);
                } else if (deleteType === 'everyone') {
                    const index = state.messages.findIndex(m => m._id === messageId);
                    if (index !== -1) {
                        state.messages[index].isDeleted = true;
                    }
                }
            });
    },
});

export const { setActiveChat, addMessage, updateMessage } = chatSlice.actions;
export default chatSlice.reducer;
